import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Type definitions for the e55 status data
export interface QuarterData {
  ppo: string;
  date: string;
  version: number | string;
  status: string;
}

export interface E55StatusItem {
  ppo: string;
  year: string | number;
  version?: string | number;
  status?: string;
  closed?: boolean;
  quarters: {
    q1?: QuarterData;
    q2?: QuarterData;
    q3?: QuarterData;
    q4?: QuarterData;
  };
}

export interface E55StatusResponse {
  inbound: E55StatusItem[];
  outbound: E55StatusItem[];
}

// Parameters for filtering the status data
export interface E55StatusParams {
  year?: string;
  ppo?: string;
  inbound?: boolean;
  open?: boolean;
}

// Query key for consistent reference
export const e55StatusQueryKey = ['e55-status'];

/**
 * Fetches e55 status data from the API
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Promise with the e55 status data
 */
const fetchE55Status = async (params?: E55StatusParams): Promise<E55StatusResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.year) {
      queryParams.append('year', params.year);
    }
    
    if (params?.ppo) {
      queryParams.append('ppo', params.ppo);
    }
    
    if (params?.inbound !== undefined) {
      queryParams.append('inbound', params.inbound.toString());
    }
    
    if (params?.open !== undefined) {
      queryParams.append('open', params.open.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/data-set/e55/status${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching e55 status data:', error);
    throw error;
  }
};

/**
 * Hook for fetching and managing e55 status data
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Query result with e55 status data, loading state, error state, and refetch function
 */
export function useE55Status(params?: E55StatusParams) {
  return useQuery<E55StatusResponse>({
    queryKey: [...e55StatusQueryKey, params],
    queryFn: () => fetchE55Status(params),
    retry: 2,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });
} 